class AnnouncementBar extends HTMLElement {
  constructor() {
    super();
    this.initBar();
  }

  async initBar() {
    this.convertCurrency();
    this.startScrolling();
    this.setupHoverPause();
  }

  formatCurrency(amount) {
    // Add commas for thousands separators and keep two decimal places
    return parseFloat(amount).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  async convertCurrency() {
    const shippingElements = this.querySelectorAll(".free-shipping-threshold");
    const threshold = this.dataset.threshold;

    if (!threshold || !shippingElements) return;

    if (!window.Shopify) {
      console.warn(
        "Shopify object not found. Currency conversion may not work correctly."
      );

      shippingElements.forEach((shippingElement) => {
        shippingElement.textContent = `${threshold}`;
      });
      return;
    }

    const shopCurrency = window.Shopify.currency.active;
    const baseCurrency = window.Shopify.currency.base;

    const conversionRate = Shopify.currency.rate || 1; // Use rate or fallback to 1
    const convertedAmount = (threshold * conversionRate).toFixed(2);
    shippingElements.forEach((shippingElement) => {
      shippingElement.textContent = `${this.formatCurrency(
        convertedAmount
      )} ${shopCurrency}`;
    });
  }

  startScrolling() {
    const track = this.querySelector(".announcement-track");
    const speed = this.dataset.speed || 10;
    if (track) {
      track.style.animation = `scroll ${speed}s linear infinite`;
    }
  }

  setupHoverPause() {
    const track = this.querySelector(".announcement-track");
    if (track) {
      track.addEventListener("mouseenter", () => {
        track.style.animationPlayState = "paused";
      });
      track.addEventListener("mouseleave", () => {
        track.style.animationPlayState = "running";
      });
    }
  }
}

customElements.define("scrolling-announcement", AnnouncementBar);

class ScrollingLogos extends HTMLElement {
  constructor() {
    super();
    this.initScrolling();
  }

  initScrolling() {
    const wrapper = this.querySelector(".scrolling-logos-wrapper");
    const speed = this.dataset.speed || 15;

    wrapper.style.animationDuration = `${speed}s`;

    // Pause animation on hover
    this.addEventListener("mouseenter", () => {
      wrapper.style.animationPlayState = "paused";
    });

    this.addEventListener("mouseleave", () => {
      wrapper.style.animationPlayState = "running";
    });
  }
}

customElements.define("scrolling-logos", ScrollingLogos);

class CollectionSlider extends HTMLElement {
  constructor() {
    super();
    this.hideSlider();
    this.initSlider();
  }

  hideSlider() {
    this.style.display = 'none';
    this.style.opacity = '0';
    this.style.transition = 'opacity 0.3s ease';
  }

  showSlider() {
    this.style.display = 'block';
    this.style.opacity = '1';
  }

  initSlider() {
    const slidesPerView = this.dataset.slides || 4;
    const spacing = this.dataset.spacing || 20;

    window.Swiper = Swiper;

    new Swiper(this, {
      loop: false,
      slidesPerView: parseInt(slidesPerView),
      spaceBetween: parseInt(spacing),
      navigation: {
        nextEl: ".swiper-button-next",
        prevEl: ".swiper-button-prev",
      },
      breakpoints: {
        "@0.00": {
          slidesPerView: 1,
          spaceBetween: 15,
        },
        "@0.75": {
          slidesPerView: 2,
          spaceBetween: 20,
        },
        "@1.00": {
          slidesPerView: 4,
          spaceBetween: 25,
        },
        "@1.50": {
          slidesPerView: 5,
          spaceBetween: 30,
        },
      },
      on: {
        init: () => {
          setTimeout(() => {
            this.showSlider();
          }, 2000); // 2-second delay
        },
      },
    });
  }
}

customElements.define("collection-slider", CollectionSlider);



class ProductImageGallery extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.initGallery();
    window.addEventListener("load", this.adjustThumbnailHeight.bind(this));
    window.addEventListener("resize", this.adjustThumbnailHeight.bind(this));
  }

  initGallery() {
    this.mainSlider = new Swiper(".main-slider", {
      slidesPerView: 1,
      spaceBetween: 10,
      loop: true,
      navigation: {
        nextEl: ".swiper-button-next",
        prevEl: ".swiper-button-prev",
      },
    });

    this.thumbnailSlider = new Swiper(".thumbnails-slider", {
      direction: "vertical",
      slidesPerView: "auto",
      spaceBetween: 10,
      freeMode: true,
      watchSlidesProgress: true,
      watchSlidesVisibility: true,
      slideToClickedSlide: true,
      mousewheel: true, // ✅ Enable scrolling with mouse
    });

    // Link both sliders
    this.mainSlider.controller.control = this.thumbnailSlider;
    this.thumbnailSlider.controller.control = this.mainSlider;

    document
      .querySelectorAll(".thumbnails-slider .swiper-slide")
      .forEach((thumb, index) => {
        thumb.addEventListener("click", () => {
          this.mainSlider.slideToLoop(index);
        });
      });

    this.adjustThumbnailHeight();
  }

  adjustThumbnailHeight() {
    const mainSliderElement = document.querySelector(".main-slider");
    const thumbnailSliderElement = document.querySelector(".thumbnails-slider");

    if (mainSliderElement && thumbnailSliderElement) {
      const mainImage = mainSliderElement.querySelector("img");
      if (mainImage && mainImage.complete) {
        thumbnailSliderElement.style.height = `${mainSliderElement.clientHeight}px`;
        this.thumbnailSlider.update();
      } else {
        mainImage.onload = () => {
          thumbnailSliderElement.style.height = `${mainSliderElement.clientHeight}px`;
          this.thumbnailSlider.update();
        };
      }
    }
  }
}

customElements.define("product-image-gallery", ProductImageGallery);

class ProductAccordion extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector(".accordion-toggle");
    this.content = this.querySelector(".accordion-content");
    this.caret = this.querySelector(".product-accordion__caret");

    if (this.button) {
      this.button.addEventListener("click", this.toggleAccordion.bind(this));
    }
  }

  toggleAccordion() {
    const parentContainer = this.closest(".product");
    const openAccordions = parentContainer.querySelectorAll(
      "product-accordion.open"
    );

    // Close other accordions
    openAccordions.forEach((accordion) => {
      if (accordion !== this) {
        accordion.closeAccordion();
      }
    });

    // Toggle this accordion
    if (this.classList.contains("open")) {
      this.closeAccordion();
    } else {
      this.openAccordion();
    }
  }

  openAccordion() {
    this.classList.add("open");
    this.content.style.maxHeight = this.content.scrollHeight + "px";
    this.button.classList.add("active");

    if (this.caret) {
      this.caret.style.transform = "rotate(180deg)"; // Rotate caret when open
    }
  }

  closeAccordion() {
    this.classList.remove("open");
    this.content.style.maxHeight = "0";
    this.button.classList.remove("active");

    if (this.caret) {
      this.caret.style.transform = "rotate(0deg)"; // Reset caret when closed
    }
  }
}

customElements.define("product-accordion", ProductAccordion);

class FreeShippingProgress extends HTMLElement {
  constructor() {
    super();
    this.threshold = parseFloat(this.dataset.threshold);
    this.freeItemThreshold = parseFloat(this.dataset.freeItemThreshold);
    this.cartTotal = parseFloat(this.dataset.cartTotal);
    this.userCurrency = this.dataset.currency;
    this.shopCurrency = this.dataset.shopCurrency;
    this.currencyRates = Shopify?.currency?.exchange_rates || {};
    this.render();
  }

  connectedCallback() {
    document.addEventListener("cart:updated", (event) => {
      this.cartTotal = event.detail.total_price / 100;
      this.updateProgress();
    });
  }

  convertCurrency(amount) {
    if (
      this.userCurrency !== this.shopCurrency &&
      this.currencyRates[this.userCurrency]
    ) {
      return amount * this.currencyRates[this.userCurrency];
    }
    return amount;
  }

  updateProgress() {
    const fill = this.querySelector(".progress-fill");
    const shippingMarker = this.querySelector(".marker.shipping");
    const giftMarker = this.querySelector(".marker.gift");

    const total = this.convertCurrency(this.cartTotal);
    const shipping = this.convertCurrency(this.threshold);
    const gift = this.convertCurrency(this.freeItemThreshold);

    let percentage = 0;
    if (total >= gift) {
      percentage = 100;
    } else if (total >= shipping) {
      percentage =
        40 + ((total - shipping) / (gift - shipping)) * (80 - 40);
    } else {
      percentage = (total / shipping) * 40;
    }

    fill.style.width = `${percentage}%`;

    if (total >= shipping) {
      shippingMarker.classList.add("achieved");
    } else {
      shippingMarker.classList.remove("achieved");
    }

    if (total >= gift) {
      giftMarker.classList.add("achieved");
    } else {
      giftMarker.classList.remove("achieved");
    }
  }

  render() {
    this.innerHTML = `
      <div class="progress-container">
        <div class="progress-bar">
          <div class="progress-fill"></div>
          <div class="marker shipping"></div>
          <div class="marker gift"></div>
        </div>
      </div>
    `;
    this.updateProgress();
  }
}

customElements.define("free-shipping-progress", FreeShippingProgress);


